import { useState, useEffect } from 'react';
import { callFirebaseFunction } from '../utils/firebaseCalls';
import { useUser } from '../contexts/UserContext';

export interface TenantSettings {
  tenantId: string;
  tenantName: string;
  features: Record<string, boolean>;
  modules: Record<string, {
    enabled: boolean;
    version: string;
    settings: Record<string, any>;
  }>;
  coachMode: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Simple React Hook for Tenant Settings
 * 
 * Clean implementation that only uses the tenants collection.
 */
export const useTenantSettings = () => {
  const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user can access tenant settings - FOR READING (all tenant users need this for menu visibility)
  const canReadTenantSettings = () => {
    return user && userProfile && (user.tenantId || isGlobalAdmin()); // All tenant users + global admins can read tenant settings
  };

  // Check if user can modify tenant settings - FOR WRITING (only admins)
  const canAccessTenantSettings = () => {
    return user && userProfile && (isTenantAdmin() || isGlobalAdmin());
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!canReadTenantSettings()) {
      setLoading(false);
      setTenantSettings(null);
      return;
    }

    loadTenantSettings();
  }, [user, userProfile]);

  const loadTenantSettings = async () => {
    try {
      // Only set loading if we don't have settings yet - prevents menu flashing
      if (!tenantSettings) {
        setLoading(true);
      }
      setError(null);

      const result = await callFirebaseFunction('getTenantSettings', {}) as { success: boolean; settings?: TenantSettings; error?: string };

      if (result && result.success && result.settings) {
        // Check if modules are missing (legacy check)
        const settings = result.settings;
        if (!settings.modules) {
          settings.modules = getFallbackTenantSettings().modules;
        }
        setTenantSettings(settings);
      } else {
        const errorMsg = result && 'error' in result ? result.error : 'Failed to load tenant settings';
        console.warn('Failed to load tenant settings, using fallback:', errorMsg);
        // Use fallback settings instead of failing
        setTenantSettings(getFallbackTenantSettings());
        setError(null); // Clear error since we have fallback
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenant settings';
      console.warn('Failed to load tenant settings, using fallback:', errorMessage);
      // Use fallback settings instead of failing
      setTenantSettings(getFallbackTenantSettings());
      setError(null); // Clear error since we have fallback
    } finally {
      setLoading(false);
    }
  };

  // Provide fallback tenant settings when Firebase function fails
  const getFallbackTenantSettings = (): TenantSettings => {
    return {
      tenantId: user?.tenantId || 'fallback-tenant',
      tenantName: 'Default Organization',
      features: {
        analytics: true,
        referrals: true,
        feedback: true
      },
      modules: {
        presentations: {
          enabled: true,
          version: '1.0.0',
          settings: {
            allowVideoUpload: true,
            allowMultipleFormats: true,
            maxPresentationsPerUser: 100
          }
        },
        consultants: {
          enabled: true,
          version: '1.0.0',
          settings: {
            allowCustomProfiles: true,
            allowBulkImport: true,
            maxConsultantsPerTenant: 1000
          }
        },
        evaluationSchemas: {
          enabled: true,
          version: '1.0.0',
          settings: {
            allowCustomSchemas: true,
            allowGlobalSchemas: true,
            maxSchemasPerTenant: 50
          }
        },
        evaluationPipeline: {
          enabled: true,
          version: '1.0.0',
          settings: {
            enableAssemblyAI: true,
            enableOpenAI: true,
            enableSpeakerIdentification: true,
            enableKnowledgeVerification: true,
            maxRetryAttempts: 3,
            timeoutMinutes: 30
          }
        },
        analytics: {
          enabled: true,
          version: '1.0.0',
          settings: {
            allowExport: true,
            allowCustomReports: true,
            retentionDays: 365,
            enableCompetencyProgression: true,
            enableScoreDistribution: true,
            enableConsultantComparison: true,
            enableVolumeMetrics: true
          }
        },
        reports: {
          enabled: true,
          version: '1.0.0',
          settings: {
            allowPDFExport: true,
            allowCustomTemplates: true,
            maxReportLength: 50,
            enableMarkdownExport: true,
            includePlaceholderCharts: true,
            enableDetailedRubricView: true
          }
        },

      },
      coachMode: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  };

  const updateTenantSettings = async (newSettings: Partial<TenantSettings> | Record<string, any>) => {
    try {
      setError(null);

      const result = await callFirebaseFunction('updateTenantSettings', {
        settings: newSettings
      }) as { success: boolean; settings?: TenantSettings; message?: string; error?: string };

      if (result && result.success && result.settings) {
        setTenantSettings(result.settings);
        return { success: true, message: result.message || 'Settings updated successfully' };
      } else {
        const errorMsg = result && 'error' in result ? result.error : 'Failed to update tenant settings';
        setError(errorMsg || 'Failed to update tenant settings');
        return { success: false, error: errorMsg };
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant settings';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Helper functions for checking tenant-specific features and modules
  const isFeatureEnabledForTenant = (featureName: string): boolean | undefined => {
    // If still loading, return undefined to signal we don't know yet
    if (loading) {
      return undefined;
    }

    // If error or no settings, return undefined to trigger fallback
    if (error || !tenantSettings?.features) {
      return undefined;
    }

    return tenantSettings.features[featureName] === true;
  };

  const isModuleEnabledForTenant = (moduleName: string): boolean | undefined => {
    // If still loading, return undefined to signal we don't know yet
    if (loading) {
      return undefined;
    }

    // If error or no settings, return undefined to trigger fallback
    if (error || !tenantSettings?.modules) {
      return undefined;
    }

    return tenantSettings.modules[moduleName]?.enabled === true;
  };

  const canTenantEnableFeature = (featureName: string) => {
    // For simplified implementation, all features can be enabled
    return true;
  };

  const canTenantEnableModule = (moduleName: string) => {
    // For simplified implementation, all modules can be enabled
    return true;
  };

  return {
    tenantSettings,
    globalSettings: null, // Simplified implementation doesn't use global settings
    loading,
    error,
    canAccessTenantSettings: canAccessTenantSettings(), // For modification (admin only)
    canReadTenantSettings: canReadTenantSettings(), // For reading (all tenant users)
    loadTenantSettings,
    updateTenantSettings,
    isFeatureEnabledForTenant,
    isModuleEnabledForTenant,
    canTenantEnableFeature,
    canTenantEnableModule
  };
};

export default useTenantSettings;